import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import PasswordInput from "../PasswordInput";
import GoogleConnectBlock from "./GoogleConnectBlock";
import { EXPERTISE_AREAS, expertiseLabel } from "../expertise";

const PHOTO_MAX_BYTES = 8 * 1024 * 1024;
const PHOTO_MAX_EDGE = 640;

function readProfilePhoto(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      reject(new Error("type"));
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      reject(new Error("size"));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(img.width || 1, img.height || 1));
      const width = Math.max(1, Math.round((img.width || 1) * scale));
      const height = Math.max(1, Math.round((img.height || 1) * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("type"));
    };
    img.src = url;
  });
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const location = useLocation();
  const [params] = useSearchParams();
  const composeLinkedIn = params.get("compose") === "linkedin";
  const [editLang, setEditLang] = useState(() => (lang === "fr" ? "fr" : "en"));
  const loc = editLang;
  const [profile, setProfile] = useState({
    name: user?.name || "",
    company: user?.company || "",
    photo_url: user?.photo_url || "",
    location: user?.location || "",
    season: user?.season || "",
    linkedin_url: user?.linkedin_url || "",
    website_url: user?.website_url || "",
    extra_links: user?.extra_links || [],
    languages: user?.languages || [],
    media_mentions: user?.media_mentions || "",
    expertise_areas: user?.expertise_areas || [],
    headline_en: user?.headline_en || (user?.lang !== "fr" ? (user?.headline || "") : ""),
    headline_fr: user?.headline_fr || (user?.lang === "fr" ? (user?.headline || "") : ""),
    tagline_en: user?.tagline_en || (user?.lang !== "fr" ? (user?.tagline || "") : ""),
    tagline_fr: user?.tagline_fr || (user?.lang === "fr" ? (user?.tagline || "") : ""),
    bio_en: user?.bio_en || (user?.lang !== "fr" ? (user?.bio || "") : ""),
    bio_fr: user?.bio_fr || (user?.lang === "fr" ? (user?.bio || "") : ""),
    expertise_details_en: user?.expertise_details_en || (user?.lang !== "fr" ? (user?.expertise_details || {}) : {}),
    expertise_details_fr: user?.expertise_details_fr || (user?.lang === "fr" ? (user?.expertise_details || {}) : {}),
    lang: user?.lang || "en",
    directory_visibility: user?.directory_visibility || (user?.directory_listed ? "public" : "hidden"),
    directory_whitelist: (user?.directory_whitelist || []).join("\n"),
  });
  const [savedProfile, setSavedProfile] = useState(false);
  const [profileErr, setProfileErr] = useState("");
  const [photoErr, setPhotoErr] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeNote, setComposeNote] = useState("");

  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [pwdErr, setPwdErr] = useState("");
  const [pwdOk, setPwdOk] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // Deep-link: scroll to #change-password if the hash is present in the URL
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 10) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
    }
  }, [location.hash]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileErr("");
    setSavedProfile(false);
    try {
      const whitelist = profile.directory_visibility === "whitelist"
        ? profile.directory_whitelist.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
        : [];
      const { data } = await api.put("/me/profile", {
        name: profile.name,
        company: profile.company,
        photo_url: profile.photo_url,
        headline_en: profile.headline_en,
        headline_fr: profile.headline_fr,
        tagline_en: profile.tagline_en,
        tagline_fr: profile.tagline_fr,
        bio_en: profile.bio_en,
        bio_fr: profile.bio_fr,
        location: profile.location,
        season: profile.season,
        linkedin_url: profile.linkedin_url,
        website_url: profile.website_url,
        extra_links: profile.extra_links,
        languages: profile.languages,
        media_mentions: profile.media_mentions,
        expertise_areas: profile.expertise_areas,
        expertise_details_en: profile.expertise_details_en,
        expertise_details_fr: profile.expertise_details_fr,
        lang: profile.lang,
        directory_visibility: profile.directory_visibility,
        directory_whitelist: whitelist,
      });
      if (data) {
        setProfile((p) => ({
          ...p,
          headline_en: data.headline_en ?? p.headline_en,
          headline_fr: data.headline_fr ?? p.headline_fr,
          tagline_en: data.tagline_en ?? p.tagline_en,
          tagline_fr: data.tagline_fr ?? p.tagline_fr,
          bio_en: data.bio_en ?? p.bio_en,
          bio_fr: data.bio_fr ?? p.bio_fr,
          expertise_details_en: data.expertise_details_en ?? p.expertise_details_en,
          expertise_details_fr: data.expertise_details_fr ?? p.expertise_details_fr,
        }));
      }
      if (profile.lang !== lang) setLang(profile.lang);
      setSavedProfile(true);
      if (refresh) refresh();
      setTimeout(() => setSavedProfile(false), 2500);
    } catch (e2) {
      setProfileErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setSavingPwd(true);
    setPwdErr("");
    setPwdOk(false);
    try {
      await api.post("/me/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      setPwdOk(true);
      setTimeout(() => setPwdOk(false), 3000);
    } catch (e2) {
      setPwdErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSavingPwd(false);
    }
  };

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="settings-heading">{t("settings.title")}</h1>
      <p className="tb-muted" style={{ marginBottom: "1.5rem" }}>{t("settings.subtitle")}</p>

      <div className="tb-block" style={{ marginBottom: "1.5rem" }}>
        <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("settings.profileTitle")}</h3>
        <form onSubmit={saveProfile} className="tb-form" data-testid="profile-form">
          {user.role === "advisor" && (
            <div className="tb-bio-compose" data-testid="settings-linkedin-compose">
              <div style={{ fontWeight: 400 }}>{t("settings.composeTitle")}</div>
              <p className="tb-muted" style={{ margin: "6px 0 10px", fontSize: "0.82rem" }}>
                {composeLinkedIn ? t("settings.composeWelcome") : t("settings.composeHint")}
              </p>
              <div className="tb-field-with-btn">
                <input
                  type="text"
                  value={profile.linkedin_url}
                  onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
                  placeholder="https://www.linkedin.com/in/…"
                  data-testid="settings-compose-linkedin"
                />
                <button
                  type="button"
                  className="tb-primary-btn"
                  disabled={composing || !profile.linkedin_url}
                  onClick={async () => {
                    setComposing(true);
                    setProfileErr("");
                    try {
                      const { data } = await api.post("/me/linkedin-compose", { linkedin_url: profile.linkedin_url });
                      const longer = (current, incoming) => {
                        const next = (incoming || "").trim();
                        const prev = (current || "").trim();
                        if (!next) return current || "";
                        if (prev.length > next.length) return current;
                        return incoming;
                      };
                      setProfile((p) => {
                        const next = {
                          ...p,
                          linkedin_url: data.linkedin_url || p.linkedin_url,
                          photo_url: data.photo_url || p.photo_url,
                          name: data.name || p.name,
                          company: data.company || p.company,
                          location: data.location || p.location,
                        };
                        const fill = (enKey, frKey, value, keepLonger) => {
                          if (!value) return;
                          const write = (key) => {
                            next[key] = keepLonger ? longer(p[key], value) : (value || p[key] || "");
                          };
                          if (loc === "fr") {
                            write(frKey);
                            if (!(p[enKey] || "").trim()) next[enKey] = next[frKey];
                          } else {
                            write(enKey);
                            if (!(p[frKey] || "").trim()) next[frKey] = next[enKey];
                          }
                        };
                        fill("headline_en", "headline_fr", data.headline, false);
                        fill("tagline_en", "tagline_fr", data.tagline, false);
                        fill("bio_en", "bio_fr", data.bio, true);
                        return next;
                      });
                      const missingTitle = !data.headline && !data.tagline;
                      if (!data.headline && !data.bio && !data.company && !data.location) setComposeNote("partial");
                      else if (data.about_truncated || missingTitle) setComposeNote("limited");
                      else setComposeNote("ok");
                    } catch (e2) {
                      setProfileErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
                    } finally {
                      setComposing(false);
                    }
                  }}
                  data-testid="settings-compose-apply"
                >
                  {composing ? t("settings.composing") : t("settings.composeApply")}
                </button>
              </div>
              {composeNote === "partial" && (
                <p className="tb-muted" style={{ margin: "8px 0 0", fontSize: "0.82rem" }} data-testid="settings-compose-partial">
                  {t("settings.composePartial")}
                </p>
              )}
              {composeNote === "limited" && (
                <p className="tb-muted" style={{ margin: "8px 0 0", fontSize: "0.82rem" }} data-testid="settings-compose-limited">
                  {t("settings.composeLimited")}
                </p>
              )}
            </div>
          )}
          <div>
            <label>{t("auth.name")}</label>
            <input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required data-testid="settings-name" />
          </div>
          <div>
            <label>{t("auth.email")}</label>
            <input type="email" value={user.email} disabled data-testid="settings-email" />
          </div>
          <div>
            <label>{t("auth.company")}</label>
            <input type="text" value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="settings-company" />
          </div>
          <div className="tb-block" style={{ background: "var(--surface)", padding: "14px 16px" }} data-testid="settings-profile-text">
            <div style={{ marginBottom: 20 }}>{t("settings.profileText")}</div>
            <div className="tb-expertise-filters" style={{ margin: "0 0 8px" }} data-testid="settings-bio-lang">
              {["en", "fr"].map((code) => (
                <button
                  type="button"
                  key={code}
                  className={`tb-chip ${editLang === code ? "active" : ""}`}
                  onClick={() => setEditLang(code)}
                  data-testid={`settings-bio-lang-${code}`}
                >
                  {code === "fr" ? t("auth.langFr") : t("auth.langEn")}
                </button>
              ))}
            </div>
            <p className="tb-muted" style={{ margin: "0 0 8px", fontSize: "0.82rem" }}>{t("settings.bioLangHint")}</p>
            <div style={{ marginTop: 24 }}>
              <label>{t("settings.headline")}</label>
              <input
                type="text"
                value={loc === "fr" ? profile.headline_fr : profile.headline_en}
                onChange={(e) => setProfile({
                  ...profile,
                  [loc === "fr" ? "headline_fr" : "headline_en"]: e.target.value,
                })}
                placeholder={t("settings.headlinePlaceholder")}
                data-testid="settings-headline"
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label>{t("settings.tagline")}</label>
              <input
                type="text"
                value={loc === "fr" ? profile.tagline_fr : profile.tagline_en}
                onChange={(e) => setProfile({
                  ...profile,
                  [loc === "fr" ? "tagline_fr" : "tagline_en"]: e.target.value,
                })}
                placeholder={t("settings.taglinePlaceholder")}
                data-testid="settings-tagline"
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label>{t("settings.bio")}</label>
              <textarea
                rows={8}
                value={loc === "fr" ? profile.bio_fr : profile.bio_en}
                onChange={(e) => setProfile({
                  ...profile,
                  [loc === "fr" ? "bio_fr" : "bio_en"]: e.target.value,
                })}
                data-testid="settings-bio"
                placeholder={t("settings.bioPlaceholder")}
              />
            </div>
          </div>
          <div>
            <label>{t("settings.location")}</label>
            <input
              type="text"
              value={profile.location}
              onChange={(e) => setProfile({ ...profile, location: e.target.value })}
              placeholder={t("settings.locationPlaceholder")}
              data-testid="settings-location"
            />
          </div>
          <div>
            <label>{t("settings.linkedin")}</label>
            <input
              type="text"
              value={profile.linkedin_url}
              onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
              placeholder="https://www.linkedin.com/in/…"
              data-testid="settings-linkedin"
            />
          </div>
          <div>
            <label>{t("settings.website")}</label>
            <input
              type="text"
              value={profile.website_url}
              onChange={(e) => setProfile({ ...profile, website_url: e.target.value })}
              placeholder="https://…"
              data-testid="settings-website"
            />
          </div>
          <div data-testid="settings-extra-links">
            <label>{t("settings.extraLinks")}</label>
            <p className="tb-muted" style={{ margin: "0 0 6px", fontSize: "0.82rem" }}>{t("settings.extraLinksHint")}</p>
            {(profile.extra_links || []).map((link, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => {
                    const extra_links = [...profile.extra_links];
                    extra_links[i] = { ...extra_links[i], label: e.target.value };
                    setProfile({ ...profile, extra_links });
                  }}
                  placeholder={t("settings.linkLabel")}
                  data-testid={`settings-link-label-${i}`}
                  style={{ flex: "1 1 140px" }}
                />
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => {
                    const extra_links = [...profile.extra_links];
                    extra_links[i] = { ...extra_links[i], url: e.target.value };
                    setProfile({ ...profile, extra_links });
                  }}
                  placeholder="https://…"
                  data-testid={`settings-link-url-${i}`}
                  style={{ flex: "2 1 220px" }}
                />
                <button
                  type="button"
                  className="tb-ghost-btn"
                  onClick={() => setProfile({ ...profile, extra_links: profile.extra_links.filter((_, idx) => idx !== i) })}
                  data-testid={`settings-link-remove-${i}`}
                >
                  {t("settings.removeLink")}
                </button>
              </div>
            ))}
            {(profile.extra_links || []).length < 8 && (
              <button
                type="button"
                className="tb-ghost-btn"
                style={{ margin: "12px 0" }}
                onClick={() => setProfile({ ...profile, extra_links: [...(profile.extra_links || []), { label: "", url: "" }] })}
                data-testid="settings-add-link"
              >
                {t("settings.addLink")}
              </button>
            )}
          </div>
          {(user.role === "advisor" || user.role === "provider") && (
            <div className="tb-block" style={{ background: "var(--surface)", padding: "14px 16px" }} data-testid="settings-expertise">
              <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: 6 }}>{t("settings.expertiseTitle")}</div>
              <p className="tb-muted" style={{ margin: "0 0 16px", fontSize: "0.82rem" }}>{t("settings.expertiseHint")}</p>
              <div className="tb-expertise-filters">
                {EXPERTISE_AREAS.map((key) => {
                  const on = profile.expertise_areas.includes(key);
                  return (
                    <button
                      type="button"
                      key={key}
                      className={`tb-chip ${on ? "active" : ""}`}
                      onClick={() => {
                        const areas = on
                          ? profile.expertise_areas.filter((k) => k !== key)
                          : [...profile.expertise_areas, key];
                        const detailsEn = { ...profile.expertise_details_en };
                        const detailsFr = { ...profile.expertise_details_fr };
                        if (on) {
                          delete detailsEn[key];
                          delete detailsFr[key];
                        }
                        setProfile({ ...profile, expertise_areas: areas, expertise_details_en: detailsEn, expertise_details_fr: detailsFr });
                      }}
                      data-testid={`settings-expertise-${key}`}
                    >
                      {expertiseLabel(t, key)}
                    </button>
                  );
                })}
              </div>
              {profile.expertise_areas.map((key) => (
                <div key={key} style={{ marginTop: 12 }}>
                  <label>{t("settings.expertiseDetailLabel", { area: expertiseLabel(t, key) })}</label>
                  <textarea
                    rows={4}
                    value={((loc === "fr" ? profile.expertise_details_fr : profile.expertise_details_en)[key] || []).join("\n")}
                    onChange={(e) => {
                      const field = loc === "fr" ? "expertise_details_fr" : "expertise_details_en";
                      setProfile({
                        ...profile,
                        [field]: {
                          ...profile[field],
                          [key]: e.target.value.split("\n"),
                        },
                      });
                    }}
                    placeholder={t("settings.expertiseDetailPlaceholder")}
                    data-testid={`settings-expertise-detail-${key}`}
                  />
                </div>
              ))}
              <div style={{ marginTop: 24 }}>
                <label>{t("settings.mediaMentions")}</label>
                <textarea
                  rows={2}
                  value={profile.media_mentions}
                  onChange={(e) => setProfile({ ...profile, media_mentions: e.target.value })}
                  placeholder={t("settings.mediaMentionsPlaceholder")}
                  data-testid="settings-media"
                />
              </div>
            </div>
          )}
          <div>
            <label>{t("settings.photoUrl")}</label>
            <p className="tb-muted" style={{ margin: "0 0 8px", fontSize: "0.82rem" }} data-testid="settings-photo-how">
              {t("settings.photoHow")}
            </p>
            {profile.photo_url && (
              <img src={profile.photo_url} alt="" className="tb-bio-profile-photo" style={{ width: 72, height: 72, marginBottom: 8, display: "block" }} data-testid="settings-photo-preview" />
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <label className="tb-ghost-btn" style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
                {t("settings.photoUpload")}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: "none" }}
                  data-testid="settings-photo-file"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = "";
                    if (!file) return;
                    setPhotoErr("");
                    try {
                      const dataUrl = await readProfilePhoto(file);
                      setProfile((p) => ({ ...p, photo_url: dataUrl }));
                    } catch (err) {
                      setPhotoErr(err && err.message === "size" ? t("settings.photoTooBig") : t("settings.photoNotImage"));
                    }
                  }}
                />
              </label>
              {profile.photo_url && (
                <button
                  type="button"
                  className="tb-ghost-btn"
                  data-testid="settings-photo-remove"
                  onClick={() => {
                    setPhotoErr("");
                    setProfile((p) => ({ ...p, photo_url: "" }));
                  }}
                >
                  {t("settings.photoRemove")}
                </button>
              )}
            </div>
            {photoErr && <div className="tb-form-error" data-testid="settings-photo-error" style={{ marginBottom: 8 }}>{photoErr}</div>}
            <input
              type="text"
              value={profile.photo_url.startsWith("data:") ? "" : profile.photo_url}
              onChange={(e) => {
                setPhotoErr("");
                setProfile({ ...profile, photo_url: e.target.value });
              }}
              placeholder={profile.photo_url.startsWith("data:") ? t("settings.photoSelected") : "https://…"}
              data-testid="settings-photo"
            />
          </div>
          <div>
            <label>{t("settings.appLang")}</label>
            <p className="tb-muted" style={{ margin: "0 0 6px", fontSize: "0.82rem" }}>{t("settings.appLangHint")}</p>
            <select value={profile.lang} onChange={(e) => setProfile({ ...profile, lang: e.target.value })} data-testid="settings-lang">
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
          <div className="tb-block" style={{ background: "var(--surface)", padding: "14px 16px" }}>
            <div style={{ marginBottom: 14 }}>{t("settings.directoryTitle")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label className="tb-choice-label" style={{ alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="directory_visibility"
                  value="public"
                  checked={profile.directory_visibility === "public"}
                  onChange={() => setProfile({ ...profile, directory_visibility: "public" })}
                  data-testid="dir-public"
                  style={{ marginTop: 4 }}
                />
                <span>
                  <span>{t("settings.directoryPublic")}</span>
                  <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 2 }}>{t("settings.directoryPublicHint")}</div>
                </span>
              </label>
              <label className="tb-choice-label" style={{ alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="directory_visibility"
                  value="whitelist"
                  checked={profile.directory_visibility === "whitelist"}
                  onChange={() => setProfile({ ...profile, directory_visibility: "whitelist" })}
                  data-testid="dir-whitelist"
                  style={{ marginTop: 4 }}
                />
                <span>
                  <span>{t("settings.directoryWhitelist")}</span>
                  <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 2 }}>{t("settings.directoryWhitelistHint")}</div>
                </span>
              </label>
              {profile.directory_visibility === "whitelist" && (
                <textarea
                  rows={3}
                  value={profile.directory_whitelist}
                  onChange={(e) => setProfile({ ...profile, directory_whitelist: e.target.value })}
                  placeholder="alice@example.com&#10;bob@example.com"
                  data-testid="dir-whitelist-emails"
                  style={{ marginLeft: 28, marginTop: 4 }}
                />
              )}
              <label className="tb-choice-label" style={{ alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="directory_visibility"
                  value="hidden"
                  checked={profile.directory_visibility === "hidden"}
                  onChange={() => setProfile({ ...profile, directory_visibility: "hidden" })}
                  data-testid="dir-hidden"
                  style={{ marginTop: 4 }}
                />
                <span>
                  <span>{t("settings.directoryHidden")}</span>
                  <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 2 }}>{t("settings.directoryHiddenHint")}</div>
                </span>
              </label>
            </div>
          </div>
          {profileErr && <div className="tb-form-error">{profileErr}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="submit" className="tb-primary-btn" disabled={savingProfile} data-testid="settings-save-profile">
              {savingProfile ? t("auth.loading") : t("settings.save")}
            </button>
            {savedProfile && <span className="tb-badge good" data-testid="settings-saved">✓ {t("settings.saved")}</span>}
          </div>
        </form>
      </div>

      <GoogleConnectBlock />

      {((user.role === "entrepreneur" || user.role === "accelerator") && !user.team_owner_id) && (
        <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="settings-team-block">
          <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>
            {user.role === "accelerator" ? t("settings.teamTitleAccelerator") : t("settings.teamTitle")}
          </h3>
          <p className="tb-muted" style={{ marginTop: 0 }}>
            {user.role === "accelerator" ? t("settings.teamHintAccelerator") : t("settings.teamHint")}
          </p>
          <Link
            to="/app/team"
            className="tb-primary-btn"
            data-testid="settings-manage-team"
            style={{ textDecoration: "none", color: "#0a0d12", display: "inline-block", width: "auto", marginTop: 16 }}
          >
            {t("settings.manageTeam")} →
          </Link>
        </div>
      )}

      {(user.role === "entrepreneur" && !user.team_owner_id) || user.role === "advisor" || user.role === "admin" ? (
        <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="settings-templates-block">
          <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("settings.templates.title")}</h3>
          <p className="tb-muted" style={{ marginTop: 0, marginBottom: 16 }}>{t("settings.templates.hint")}</p>
          <Link
            to="/app/settings/templates"
            className="tb-primary-btn"
            data-testid="settings-manage-templates"
            style={{ textDecoration: "none", color: "#0a0d12", display: "inline-block", width: "auto" }}
          >
            {t("settings.templates.manage")} →
          </Link>
        </div>
      ) : null}

      <div className="tb-block" id="change-password">
        <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("settings.passwordTitle")}</h3>
        <form onSubmit={changePwd} className="tb-form" data-testid="password-form">
          <div>
            <label>{t("settings.currentPassword")}</label>
            <PasswordInput
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              required
              testid="settings-current-pwd"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label>{t("auth.newPassword")}</label>
            <PasswordInput
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              required
              minLength={6}
              testid="settings-new-pwd"
              autoComplete="new-password"
            />
          </div>
          {pwdErr && <div className="tb-form-error">{pwdErr}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="submit" className="tb-primary-btn" disabled={savingPwd} data-testid="settings-save-pwd">
              {savingPwd ? t("auth.loading") : t("settings.updatePassword")}
            </button>
            {pwdOk && <span className="tb-badge good" data-testid="settings-pwd-ok">✓ {t("settings.passwordUpdated")}</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
